import { useQueryClient } from '@tanstack/react-query';
import { effectivePermissionSetHas } from '@utazzo/shared';
import { cn, UzButton, UzEmptyState, UzTabs } from '@utazzo/ui';
import {
  Activity,
  BadgeIndianRupee,
  FileDown,
  Layers,
  LayoutDashboard,
  MapPin,
  Maximize2,
  Megaphone,
  RefreshCw,
  Share2,
  ShieldOff,
  Sprout,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ActiveStatusTable } from '../components/ActiveStatusTable';
import { CampaignContactabilityTable } from '../components/CampaignContactabilityTable';
import { ContactabilityTables } from '../components/ContactabilityTables';
import { CostFilter } from '../components/CostFilter';
import { DailyCapTable } from '../components/DailyCapTable';
import { DestinationsTable } from '../components/DestinationsTable';
import { LeadStatusTable } from '../components/LeadStatusTable';
import { MarketingOverview } from '../components/MarketingOverview';
import { MarketingWorkbook } from '../components/MarketingWorkbook';
import { MetaAdPerformanceCard } from '../components/MetaAdPerformanceCard';
import { ForceExpandContext } from '../components/month-table';
import { MonthFilter } from '../components/MonthFilter';
import { QueryChannelPanel } from '../components/QueryChannelPanel';
import { parseCostsParam } from '../cost-selection';
import {
  LEAD_TYPE_LABEL,
  MarketingLeadTypeContext,
  type MarketingReportSection,
  resolveSection,
  visibleSections,
  useMarketingLeadType,
} from '../lead-type';
import { monthLabel, parseMonthsParam, serialiseMonths } from '../month-selection';
import {
  useMarketingActiveStatus,
  useMarketingCampaignContactability,
  useMarketingChannels,
  useMarketingContactability,
  useMarketingDestinations,
  useMarketingLeadStatus,
  useMarketingQueryProfit,
  useMarketingSpend,
} from '../use-marketing-reports';

import type { LpsAssignedWindow } from '@/features/lead-parsing/components/LpsDestinationSummary';
import type { UzDateRange, UzDateRangePreset } from '@utazzo/ui';

import { useCanExportData, useCurrentUser } from '@/features/auth/use-auth';
import { LpsChannelSummary } from '@/features/lead-parsing/components/LpsChannelSummary';
// "Leads by destination and agent" is hidden for now (client request) - re-enable
// its import + the commented <LpsDestinationSummary> below to bring it back.
// import { LpsDestinationSummary } from '@/features/lead-parsing/components/LpsDestinationSummary';
import {
  resolveLeadDateRange,
  type LeadDateRangePresetKey,
} from '@/features/leads/lead-date-range';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { ReportSurfaceContext } from '@/features/reports/components/ReportSection';
import { exportTablesAsExcel } from '@/features/reports/reports-excel';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

/**
 * This report's cards carry the full-screen toggle; the other report pages'
 * cards do not, and are left exactly as they were.
 */
const CARD_SURFACE = { surface: 'card', expandable: true } as const;

/**
 * LPS-style lead intake breakdown, shown BELOW the daily requirement and OUTSIDE
 * the tabs, so it is visible on every section. For now only "Leads by destination
 * and channel (source)" - "Leads by destination and agent" is commented out per
 * the client. Reads the LPS inbound counts under the Marketing Report's OWN
 * permission (variant="marketing"), follows the All / Inorganic / Organic switch,
 * and carries its own date-range picker (defaults to this month).
 */
const MarketingLeadIntake = (): React.JSX.Element => {
  const leadType = useMarketingLeadType();
  const [preset, setPreset] = useState<LeadDateRangePresetKey>('this_month');
  const [range, setRange] = useState<UzDateRange>(() =>
    resolveLeadDateRange('this_month', null, null),
  );
  const lpsWindow: LpsAssignedWindow = useMemo(
    () => ({
      preset,
      range,
      onPresetSelect: (p: UzDateRangePreset) => {
        setPreset(p.value as LeadDateRangePresetKey);
        setRange(p.range());
      },
      onValueChange: (r: UzDateRange) => {
        setPreset('custom');
        setRange(r);
      },
    }),
    [preset, range],
  );
  return (
    <div className="my-4 flex flex-col gap-4">
      {/* "Leads by destination and agent" hidden for now (client request):
          <LpsDestinationSummary window={lpsWindow} leadType={leadType} variant="marketing" /> */}
      <LpsChannelSummary window={lpsWindow} leadType={leadType} variant="marketing" />
    </div>
  );
};

const SECTION_ICON: Record<MarketingReportSection, React.ReactNode> = {
  all: <Layers className="h-4 w-4" />,
  inorganic: <BadgeIndianRupee className="h-4 w-4" />,
  organic: <Sprout className="h-4 w-4" />,
};

const TAB_VALUES = ['overview', 'channels', 'destinations', 'campaigns', 'status'] as const;
type MarketingTab = (typeof TAB_VALUES)[number];

/**
 * Marketing report - "Query Profit and Spend". Sibling of the Daily Leads
 * Tracker (`/reports/leads`): a toolbar (refresh / export) and top tabs.
 *
 * The Overview is the whole workbook on one screen (see MarketingOverview):
 * headline tiles, the Query Profit and Spend sheet, then every table the other
 * tabs hold, each loading as it is scrolled to. The tabs stay as the focused
 * view of one part - the same table, read without the rest in the way.
 *
 * Two pickers scope every tab - which whole IST MONTHS (multi-select,
 * `?months=`) and which COSTS count toward Total Spend (`?costs=`). Ad spend
 * itself is captured per adset on the Campaigns tab and rolls up into the sheet.
 *
 * A month missing a required cost is not locked away any more: its
 * spend-based rows read "-" until the figure is typed, and the sheet says which
 * figure that is.
 *
 * Above the tabs sit the report's SECTIONS (`?type=`), split on the lead's
 * Query Type: Inorganic - the paid leads, where ad spend and every cost head
 * belong - Organic - leads that came to us on their own, so that section has
 * no cost side, no cost picker and no Campaigns tab - and All, every lead
 * together with the full cost side, as the report read before the split.
 *
 * Each section is its own permission (`ops.marketing_reports_<section>.read`,
 * like the Settings tabs): the switch shows only the sections the caller
 * holds, and `?type=` for one they do not hold falls back to the first one
 * they do. Every table on the page reads the section from
 * MarketingLeadTypeContext, which is why the page is a thin provider around
 * the report itself: the report's own reads need it too, and none of them
 * fires until the section is known to be allowed.
 */
export const MarketingReportPage = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const me = useCurrentUser();
  const permissions = useMyEffectivePermissions();
  const isAdmin = me?.role === 'ADMIN';
  const sections = visibleSections(permissions, isAdmin);
  const section = resolveSection(params.get('type'), sections);

  // /me still loading: render nothing yet rather than a section the API might
  // refuse, or a "no access" message that is about to be wrong.
  if (!isAdmin && permissions === undefined) {
    return (
      <div className="space-y-3">
        <div className="bg-muted h-8 w-60 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded-xl" />
      </div>
    );
  }
  if (!section) {
    return (
      <UzEmptyState
        icon={<ShieldOff className="text-muted-foreground h-8 w-8" />}
        title="No marketing report section granted"
        description="You can open this page, but no section of it (All, Inorganic or Organic) has been granted to you. Ask an admin to grant one from Roles & permissions."
      />
    );
  }
  return (
    <MarketingLeadTypeContext.Provider value={section}>
      <MarketingReport sections={sections} />
    </MarketingLeadTypeContext.Provider>
  );
};

const MarketingReport = ({
  sections,
}: {
  /** The sections the caller holds, in switch order - never empty here. */
  sections: MarketingReportSection[];
}): React.JSX.Element => {
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();
  const months = parseMonthsParam(params.get('months'));
  const costs = parseCostsParam(params.get('costs'));
  const leadType = useMarketingLeadType();
  const organic = leadType === 'organic';
  const setLeadType = (next: MarketingReportSection): void => {
    writeParams((p) => {
      // Always explicit: which section opens WITHOUT the param depends on what
      // the caller holds, so leaving it off would not reliably mean "All".
      p.set('type', next);
      // The Campaigns tab is Inorganic-only - land Organic on the Overview.
      if (next === 'organic' && p.get('tab') === 'campaigns') p.set('tab', 'overview');
    });
  };

  // The cost gate and the adset capture are Inorganic's (and All's, which
  // holds the Inorganic leads) - never Organic's.
  const spend = useMarketingSpend(organic ? null : months, costs);
  const profit = useMarketingQueryProfit(months, costs);

  // Entering cost data is a write on the marketing-reports module. ADMIN
  // bypasses, matching how the API guard treats the same code.
  const me = useCurrentUser();
  const myPerms = useMyEffectivePermissions();
  const canEditSpend =
    me?.role === 'ADMIN' ||
    (myPerms instanceof Set && effectivePermissionSetHas(myPerms, 'ops.marketing_reports.write'));

  // The full-screen overlay: every table at once, in one scroll. Opened from
  // the toolbar and closed with Escape, so it holds no URL state of its own.
  const [workbookOpen, setWorkbookOpen] = useState(false);

  const rawTab = params.get('tab');
  const tab: MarketingTab =
    TAB_VALUES.includes(rawTab as MarketingTab) && !(organic && rawTab === 'campaigns')
      ? (rawTab as MarketingTab)
      : 'overview';
  // Merges into the LIVE URL - any grid state on the open tab is written by
  // nuqs and is invisible to React Router's params snapshot.
  const setTab = (next: MarketingTab): void => {
    writeParams((p) => p.set('tab', next));
  };

  // Tab-scoped: each aggregate only fires once its tab is opened.
  const channels = useMarketingChannels(tab === 'channels' ? months : null);
  const leadStatus = useMarketingLeadStatus(tab === 'status' ? months : null);
  const contactability = useMarketingContactability(tab === 'status' ? months : null);
  const activeStatus = useMarketingActiveStatus(tab === 'status' ? months : null);
  const destinations = useMarketingDestinations(tab === 'destinations' ? months : null);
  const campaigns = useMarketingCampaignContactability(tab === 'campaigns' ? months : null);
  // ----- Toolbar: refresh + per-tab CSV + "updated ago" -----
  const queryClient = useQueryClient();
  const refreshing =
    spend.isFetching ||
    profit.isFetching ||
    channels.isFetching ||
    leadStatus.isFetching ||
    contactability.isFetching ||
    activeStatus.isFetching ||
    destinations.isFetching ||
    campaigns.isFetching;
  const onRefresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['marketing-reports'] });
  };
  // Re-render every 30s so the relative "updated ago" label stays current.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setClockTick((tick) => tick + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const updatedAgo = ((): string | null => {
    if (!profit.dataUpdatedAt) return null;
    const mins = Math.floor((Date.now() - profit.dataUpdatedAt) / 60_000);
    if (mins <= 0) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  })();

  const canExport = useCanExportData();
  const tabsRef = useRef<HTMLDivElement>(null);
  // Exports what is ON SCREEN: the open tab's tables, with the month and cost
  // filters, the sort and the expanded groups already applied to them.
  // Groups are forced open for the moment of the export: the file is read off
  // the rendered tables, so anything folded would simply be missing from it.
  const [exporting, setExporting] = useState(false);
  const onExportTab = async (): Promise<void> => {
    if (!tabsRef.current) return;
    setExporting(true);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const exported = await exportTablesAsExcel(
        tabsRef.current,
        `marketing-${leadType}-${tab}-${serialiseMonths(months)}.xlsx`,
        'Marketing Report',
      );
      if (!exported) toast.error('No tables to export in this tab yet.');
    } finally {
      setExporting(false);
    }
  };

  const overviewTab = (
    <MarketingOverview
      months={months}
      profit={profit.data}
      spend={spend.data}
      loading={profit.isLoading || spend.isLoading}
      canEdit={canEditSpend}
    />
  );

  const channelsTab = (
    <div className="space-y-4 pt-2">
      {/* KPI tiles removed (client request) - the channel breakdown table only. */}
      <QueryChannelPanel channels={channels.data} loading={channels.isLoading} showKpis={false} />
    </div>
  );

  const destinationsTab = (
    <div className="space-y-4 pt-2">
      <DestinationsTable data={destinations.data} loading={destinations.isLoading} />
    </div>
  );

  const campaignsTab = (
    <div className="space-y-4 pt-2">
      <MetaAdPerformanceCard months={months} canSync={canEditSpend} />
      <CampaignContactabilityTable data={campaigns.data} loading={campaigns.isLoading} />
    </div>
  );

  const statusTab = (
    <div className="space-y-4 pt-2">
      <LeadStatusTable status={leadStatus.data} loading={leadStatus.isLoading} />
      <ActiveStatusTable data={activeStatus.data} loading={activeStatus.isLoading} />
      <ContactabilityTables data={contactability.data} loading={contactability.isLoading} />
    </div>
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-foreground text-lg font-semibold">Marketing Reports</h1>
          <p className="text-muted-foreground text-sm">
            {organic ? 'Organic leads' : 'Query profit and spend'} for{' '}
            {months.map(monthLabel).join(', ')}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Costs only exist in the Inorganic section. */}
          {!organic && <CostFilter />}
          <MonthFilter />
        </div>
      </header>

      <ReportSurfaceContext.Provider value={CARD_SURFACE}>
        <ForceExpandContext.Provider value={exporting}>
          <UzTabs<MarketingReportSection>
            value={leadType}
            onValueChange={setLeadType}
            tabs={sections.map((value) => ({
              value,
              label: LEAD_TYPE_LABEL[value],
              icon: SECTION_ICON[value],
              content: null,
            }))}
          />
          {/* Today's caps, under the section switch: the same sheet whichever
              section is open, since a cap is per seller, not per lead type. */}
          <div className="my-4">
            <DailyCapTable />
          </div>
          {/* Lead intake (by source) below the daily requirement, outside the
              tabs, so it shows on every section. */}
          <MarketingLeadIntake />
          <div ref={tabsRef}>
            <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
              {updatedAgo && (
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  Updated {updatedAgo}
                </span>
              )}
              <UzButton
                variant="outline"
                size="sm"
                onClick={() => setWorkbookOpen(true)}
                title="Every table full screen, in one scroll"
              >
                <Maximize2 className="mr-1.5 h-4 w-4" />
                Full Screen
              </UzButton>
              <UzButton
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={refreshing}
                title="Refresh report data"
              >
                <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
              </UzButton>
              {canExport && (
                <UzButton
                  variant="outline"
                  size="sm"
                  onClick={() => void onExportTab()}
                  disabled={spend.isLoading || profit.isLoading}
                >
                  <FileDown className="mr-1.5 h-4 w-4" />
                  Export Excel
                </UzButton>
              )}
            </div>

            <UzTabs<MarketingTab>
              value={tab}
              onValueChange={setTab}
              className="[&_[role=tablist]]:h-auto [&_[role=tablist]]:max-w-full [&_[role=tablist]]:flex-wrap [&_[role=tablist]]:justify-start [&_[role=tablist]]:gap-1"
              tabs={[
                {
                  value: 'overview',
                  label: 'Overview',
                  icon: <LayoutDashboard className="h-4 w-4" />,
                  content: overviewTab,
                },
                {
                  value: 'channels',
                  label: 'Query Channel',
                  icon: <Share2 className="h-4 w-4" />,
                  content: channelsTab,
                },
                {
                  value: 'destinations',
                  label: 'Destinations',
                  icon: <MapPin className="h-4 w-4" />,
                  content: destinationsTab,
                },
                ...(organic
                  ? []
                  : [
                      {
                        value: 'campaigns' as const,
                        label: 'Campaigns',
                        icon: <Megaphone className="h-4 w-4" />,
                        content: campaignsTab,
                      },
                    ]),
                {
                  value: 'status',
                  label: 'Lead Status',
                  icon: <Activity className="h-4 w-4" />,
                  content: statusTab,
                },
              ]}
            />
          </div>
        </ForceExpandContext.Provider>
      </ReportSurfaceContext.Provider>

      {workbookOpen && (
        <MarketingWorkbook
          months={months}
          costs={costs}
          canEdit={canEditSpend}
          canExport={canExport}
          onClose={() => setWorkbookOpen(false)}
        />
      )}
    </div>
  );
};
